// noinspection JSNonASCIINames

import {booleanAttribute, ChangeDetectionStrategy, Component, computed, DestroyRef, effect, ElementRef, EventEmitter, Injectable, input, Input, Output, Signal} from '@angular/core';
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {FormControl, FormGroup, ReactiveFormsModule} from "@angular/forms";
import {MatFormField, MatInput, MatInputModule} from "@angular/material/input";
import {AgendaelementId, AgendaelementModell, FinnSakerOgMalsakerResultatModell, GruppeId, GruppeMedlemListeModell, HtmlTekst, InkludertGenerertDokument, LitenIkkeTomStreng, MalsakModell, MøteModellEnkel, NySakBeslutningsforslagSpesifikasjon, OpprettSakSpesifikasjon, SakRessursClient, SakstypeModell, SakSøkTreffModell, TemaModell} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {NyeBeslutningerSkjemaKomponent} from "../../../beslutning/komponenter/nye-beslutninger-skjema/nye-beslutninger-skjema";
import {MatOptgroup, MatOption} from "@angular/material/select";
import {VelgMedlemKomponent} from "web-ng2/delt/komponenter/velg-medlem/velg-medlem";
import {erIkkeNil} from "felles-ng2/støttefunksjoner/nilsjekk";
import {VelgTemaKomponent} from "../velg-tema/velg-tema";
import {debounceTime, filter, first, mergeWith, Subject, switchMap} from "rxjs";
import {Temporal} from "temporal-polyfill";
import {VarighetMinutterVelgerKomponent} from "web-ng2/delt/komponenter/varighet-minutter-velger/varighet-minutter-velger";
import {durationFraMinutter, durationTilMinutter} from "felles-ng2/støttefunksjoner/temporal";
import {VelgDagFraFlerdagsmøteKomponent} from "../velg-dag-fra-flerdagsmøte/velg-dag-fra-flerdagsmøte";
import {KlokkeslettVelgerKomponent} from "web-ng2/delt/komponenter/klokkeslett-velger/klokkeslett-velger";
import {MatIcon} from "@angular/material/icon";
import {MatButton, MatIconButton} from "@angular/material/button";
import {FeltValidatorer} from "felles-ng2/infrastruktur/angular/feltValidatorer";
import {VelgSakstypeKomponent} from "web-ng2/moduler/sak/komponenter/velg-sakstype/velg-sakstype";
import {TranslateModule} from "@ngx-translate/core";
import {StandardValideringsfeilPipe} from "felles-ng2/pipes/StandardValideringsfeilPipe";
import {nyUuid} from "felles-ng2/støttefunksjoner/uuidGenerator";
import {MatAutocomplete, MatAutocompleteTrigger} from "@angular/material/autocomplete";
import {MatTooltip} from "@angular/material/tooltip";
import {map} from "rxjs/operators";
import {takeUntilDestroyed, toSignal} from "@angular/core/rxjs-interop";
import {VelgGenerertDokumentKomponent} from "web-ng2/moduler/sak/komponenter/velg-generert-dokument/velg-generert-dokument";
import {FormaterZonedDateTimePipe} from "felles-ng2/pipes/FormaterZonedDateTimePipe";
import {erFlerdagsmøte} from "felles-ng2/modell/møteModellHjelpere";
import {harFelt} from "felles-ng2/støttefunksjoner/objekter";
import {scrollTilSenter} from "web-ng2/delt/dom/scrollIntoView";
import {erIkkeTomStreng, erStreng, førsteIkkeTommeStreng, trimTilNull} from "felles-ng2/støttefunksjoner/strenger";
import {LokaltDatalager} from "felles-ng2/tjenester/LokaltDatalager";
import {sakVarighetSomDuration, startklokkeslettSakSomUTC} from "felles-ng2/modell/sakModellHjelpere";
import {Tidssoneinformasjon} from "felles-ng2/tjenester/i18n/GjeldendeTidssone";
import {RiktekstRedigeringKomponent} from "web-ng2/delt/komponenter/skjema/riktekst-redigering/riktekst-redigering";
import PlainTime = Temporal.PlainTime;
import Duration = Temporal.Duration;

const debug = initDebugger('SakNyKontroller');

export type NySakSkjema = ReturnType<typeof SakNyKontroller.opprettSkjema>;
export type NySakSkjemaVerdier = NySakSkjema['value'];


@Component({
    selector: 'sak-ny',
    templateUrl: './sak-ny.html',
    styleUrl: './sak-ny.scss',
    host: {
        '[class.er-flerdagsmøte]': 'erFlerdagsmoete()',
        '[class.er-pause]': 'skjema.value.erPause',
        '[class.viser-detaljer]': 'viserDetaljer',
    },
    imports: [
        ReactiveFormsModule,
        MatFormField,
        MatInputModule,
        MatInput,
        MatAutocompleteTrigger,
        MatAutocomplete,
        MatOptgroup,
        MatTooltip,
        MatOption,
        MatButton,
        MatIcon,
        TranslateModule,
        StandardValideringsfeilPipe,
        VelgMedlemKomponent,
        VelgDagFraFlerdagsmøteKomponent,
        KlokkeslettVelgerKomponent,
        VarighetMinutterVelgerKomponent,
        VelgSakstypeKomponent,
        VelgTemaKomponent,
        VelgGenerertDokumentKomponent,
        FormaterZonedDateTimePipe,
        MatIconButton,
        RiktekstRedigeringKomponent,
        NyeBeslutningerSkjemaKomponent,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SakNyKontroller {
    static visDetaljerNøkkel = 'nySakVisDetaljer'

    /** Oppretter nytt tomt skjema */
    static opprettSkjema() {
        return new FormGroup({
            id: new FormControl<AgendaelementId>(nyUuid(), {nonNullable: true}),
            tittel: new FormControl<LitenIkkeTomStreng>('', {validators: FeltValidatorer.litenStreng}),
            ansvarlig: new FormControl<GruppeMedlemListeModell | null>(null),
            varighet: new FormControl<Duration | null>(null),
            sakstype: new FormControl<SakstypeModell | null>(null),
            tema: new FormControl<TemaModell | null>(null),
            inkludertGenerertDokument: new FormControl<InkludertGenerertDokument | null>(null),
            beskrivelse: new FormControl<HtmlTekst>('', {validators: FeltValidatorer.htmlTekst}),
            beslutningsforslag: NyeBeslutningerSkjemaKomponent.opprettSkjema(),
            erPause: new FormControl<boolean>(false),
            valgtSaksmal: new FormControl<MalsakEllerSak | null>(null),
            kopierFraValgtSaksmal: new FormControl(false),
            //kun relevante ved flerdagsmøter
            dag: new FormControl<number | null>(0),
            startklokkeslett: new FormControl<PlainTime | null>(null)
        });
    }

    static somOpprettSakSpesifikasjon(skjemaverdier: NySakSkjemaVerdier, møte: Pick<MøteModellEnkel, 'id' | 'dato'>, brukersTidssone: Tidssoneinformasjon): OpprettSakSpesifikasjon {
        return {
            moeteId: møte.id,
            id: skjemaverdier.id!,
            tittel: skjemaverdier.tittel!,
            beskrivelse: skjemaverdier.beskrivelse ?? '',
            ansvarligId: skjemaverdier.ansvarlig?.id ?? null,
            temaId: skjemaverdier.tema?.id ?? null,
            sakstypeId: skjemaverdier.sakstype?.id ?? null,
            varighet: erIkkeNil(skjemaverdier.varighet) ? durationTilMinutter(skjemaverdier.varighet) : null,
            inkludertGenerertDokument: skjemaverdier.inkludertGenerertDokument ?? null,
            beslutningsforslag: NyeBeslutningerSkjemaKomponent.somSpesifikasjoner(skjemaverdier.beslutningsforslag!),
            erPause: skjemaverdier.erPause!,
            kopierFraSakId: skjemaverdier.kopierFraValgtSaksmal && !erMalsak(skjemaverdier.valgtSaksmal!) ? skjemaverdier.valgtSaksmal!.id : null,
            kopierFraMalsakId: skjemaverdier.kopierFraValgtSaksmal && erMalsak(skjemaverdier.valgtSaksmal!) ? skjemaverdier.valgtSaksmal!.id : null,
            //kun relevante ved flerdagsmøter
            dag: skjemaverdier.dag ?? null,
            startklokkeslett: startklokkeslettSakSomUTC(skjemaverdier.startklokkeslett, skjemaverdier.dag, møte, brukersTidssone)?.toJSON() ?? null
        };
    }

    @Output() vedSlett = new EventEmitter<SakNyKontroller>();
    //merk at dette er ut-siden av toveisbinding for viserDetaljer
    @Output() viserDetaljerChange = new EventEmitter<boolean>();

    moete = input.required<MøteModellEnkel>();
    tilleggsdata = input.required<NySakSkjemaTilleggsdata>();
    /** dag satt på forrige sak, dersom møte er flerdagsmøte, brukes for å beregne initiell verdi for dag-feltet */
    forrigeSakDag = input<number>(0);
    @Input({transform: booleanAttribute}) viserDetaljer = false;

    saksmalertreff!: Signal<FinnSakerOgMalsakerResultatModell | null>;

    //flerdagsmøte-relatert
    erFlerdagsmoete = computed(() => erFlerdagsmøte(this.moete()));
    /** valg dag som signal - for bruk i beregningen av visStartklokkeslettvelger */
    dag!: Signal<number | null>;
    /** Viser klokkeslettvelger dersom denne saken er plassert på en senere dag enn foregående */
    visStartklokkeslettvelger = computed(() => (this.dag() ?? 0) > (this.forrigeSakDag() ?? 0));
    /** Vis advarsel dersom denne saken er plassert på en tidligere dag enn forrige sak */
    dagTidligereEnnForrigeSak = computed(() => (this.dag() ?? 0) < (this.forrigeSakDag() ?? 0));

    /** observable som benyttes for å sørge for at malsak-søket kjøres når bruker klikker inn i tittel-feltet.
     * kunne alternativt benyttet rxjs fromEvent(<ref til input-felt>, 'focus'), men det betinger tilgang til dom-elem og
     * oppsett i ngAfterViewInit() og blir totalt sett ikke mindre kode.. */
    private tittelfeltFokus = new Subject<void>();
    private _skjema!: NySakSkjema;

    constructor(private el: ElementRef, private avhengigheter: SakNyKontrollerAvhengigheter, private destroyRef: DestroyRef, private lokaltDatalager: LokaltDatalager) {
        effect(() => {
            //sørger for at dag ikke er satt til en tidligere dag en foregående saker
            //merk at denne sjekken kun skjer når forrige sak.dag endres
            if (this.forrigeSakDag() > (this.skjema.value.dag ?? 0))
                this.skjema.controls.dag.setValue(this.forrigeSakDag())
        }); //etter oppdatering til material 18 som benytter signal i sine komp vil setValue føre til signal.set

        //vi husker om bruker ønsker å se detaljer i skjemaet eller ei..
        this.lokaltDatalager.hentBoolsk(SakNyKontroller.visDetaljerNøkkel)
          .then(visDetaljer => this.viserDetaljer = visDetaljer);
    }

    @Input({required: true}) set skjema(skjema: NySakSkjema) {
        this._skjema = skjema;
        this.dag = toSignal(this.skjema.controls.dag.valueChanges
          .pipe(takeUntilDestroyed(this.destroyRef)), {initialValue: null, manualCleanup: true});

        this.saksmalertreff = toSignal(this.skjema.controls.tittel.valueChanges
          .pipe(
            //ikke utfør søk ved tom tittel-tekst (uten denne blir finnSakerSomMaler kalt når komponenten initialiserer)
            //merk at når bruker klikker på innslag i saksmalertreff listen vil dette objektet bli levert til tittel-feltet
            //denne leveransen skal vi ignorere her, derav erStreng(..) filteret
            filter(verdi => erStreng(verdi) && erIkkeTomStreng(verdi)),
            //ikke utfør søk før 300ms etter siste tastetrykk
            debounceTime(300),
            //sørger for å populerer saksmalertreff når bruker klikker inn i feltet slik at saksmallisten vises initielt
            mergeWith(this.tittelfeltFokus.pipe(first(), map(() => ''))),
            //utfør søk
            switchMap(søkestreng => this.avhengigheter.finnSakerSomMaler(this.moete().eiendeGruppeId, søkestreng!)),
            takeUntilDestroyed(this.destroyRef)
          ), {initialValue: null, manualCleanup: true});

        this.skjema.controls.sakstype.valueChanges
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe(valgtType => this.skjema.controls.erPause.setValue(VelgSakstypeKomponent.erPausesak(valgtType)));
    }

    get skjema(): NySakSkjema {
        return this._skjema
    }

    tittelfeltFokusert() {
        this.tittelfeltFokus.next();
    }

    async saksforslagValgt(valgt: MalsakEllerSak) {
        debug('saksforslagValgt', valgt)
        if (!valgt) return;

        if (erMalsak(valgt))
            this.populerFraMalsak(valgt);
        else
            await this.populerFraEksisterendeSak(valgt as SakSøkTreffModell);
    }

    slett() {
        this.vedSlett.emit(this);
    }

    visEllerSkjulDetaljer() {
        this.viserDetaljer = !this.viserDetaljer;
        this.viserDetaljerChange.emit(this.viserDetaljer);
        this.lokaltDatalager.lagreBoolsk(SakNyKontroller.visDetaljerNøkkel, this.viserDetaljer);
        //når klikke på "detaljer" fører til at alle saker ekspanderer må vi sørge for å scrolle til den saken bruker klikket på
        if (this.viserDetaljer) scrollTilSenter(this.el.nativeElement, 100); //vent 100ms for at sakene skal ha rukket å ekspandere
    }

    // private hjelpemetoder

    private populerFraMalsak(mal: MalsakModell) {
        this.skjema.patchValue({
            valgtSaksmal: mal,
            kopierFraValgtSaksmal: true,

            tittel: mal.tittel,
            beskrivelse: mal.beskrivelse,
            tema: mal.tema,
            sakstype: mal.sakstype,
            ansvarlig: mal.ansvarlig,
            varighet: durationFraMinutter(mal.varighet),
            inkludertGenerertDokument: mal.inkludertGenerertDokument
        });

        // Legg til beslutningsforslag dersom malen inneholder et
        const beslutningsforslagArray = this.skjema.controls.beslutningsforslag;
        beslutningsforslagArray.clear();

        if (erIkkeTomStreng(mal.beslutningsforslagtittel) || erIkkeTomStreng(mal.beslutningsforslagbeskrivelse)) {
            const nyBeslutning = NyeBeslutningerSkjemaKomponent.opprettBeslutningSkjema();
            nyBeslutning.patchValue({
                beslutningskjema: {
                    tittel: mal.beslutningsforslagtittel ?? '',
                    beskrivelse: mal.beslutningsforslagbeskrivelse ?? ''
                }
            });
            beslutningsforslagArray.insert(0, nyBeslutning);
        }
    }

    private async populerFraEksisterendeSak(søketreff: SakSøkTreffModell) {
        this.skjema.patchValue({
            valgtSaksmal: søketreff,
            kopierFraValgtSaksmal: true,

            tittel: søketreff.tittel,
            beskrivelse: søketreff.beskrivelse,
        });

        //hent sak fra server og populer ny sak med data fra denne når lastet
        const sak = await this.avhengigheter.hentSak(søketreff.id)

        // Populer beslutningsforslag fra alle beslutninger i saken
        const beslutningsforslagArray = this.skjema.controls.beslutningsforslag;
        beslutningsforslagArray.clear();

        [...sak.beslutninger] //kopi av array siden reverse endrer selve arrayen
          .reverse()
          .forEach(beslutning => {
              const nyBeslutning = NyeBeslutningerSkjemaKomponent.opprettBeslutningSkjema();
              nyBeslutning.patchValue({
                  beslutningskjema: {
                      tittel: førsteIkkeTommeStreng(beslutning.tittelForslag, beslutning.tittel),
                      beskrivelse: førsteIkkeTommeStreng(beslutning.beskrivelseForslag, beslutning.beskrivelse)
                  }
              });
              beslutningsforslagArray.insert(0, nyBeslutning);
          });

        this.skjema.patchValue({
            tema: sak.tema,
            sakstype: sak.sakstype,
            ansvarlig: sak.ansvarlig,
            varighet: sakVarighetSomDuration(sak)
        });
    }
}

function erMalsak(kandidat: MalsakEllerSak): kandidat is MalsakModell {
    return harFelt(kandidat, 'standardIMoete');
}

export interface NySakSkjemaTilleggsdata {
    gruppemedlemmer: GruppeMedlemListeModell[];
    sakstyper: SakstypeModell[];
    temaer: TemaModell[];
}

@Injectable()
export class SakNyKontrollerAvhengigheter {

    constructor(private sakRessurs: SakRessursClient) {
    }

    finnSakerSomMaler(gruppeId: GruppeId, søkestreng: string): Promise<FinnSakerOgMalsakerResultatModell> {
        return this.sakRessurs.finnSakerSomMaler({gruppeId, soekestreng: søkestreng}).somPromise();
    }

    hentSak(id: AgendaelementId): Promise<AgendaelementModell> {
        return this.sakRessurs.hent(id).somPromise();
    }
}

type MalsakEllerSak = SakSøkTreffModell | MalsakModell;
